import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment-timezone';
import { Fragment, KeyboardEvent, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import {
	IVolunteerAttendance,
	VolunteerAttendanceStatus,
	buildContactFilter,
	useVolunteerAttendanceQuery,
	useVolunteerRolesQuery,
	useVolunteerSummaryQuery,
} from '../../../../../queries/Volunteer';
import { MultiColorPillProps, Pill } from '../../../../components-v2/Pill';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Button } from '../../../../components-v2/Button';
import { ROUTES } from '../../../../constants/routes';
import { styleSheet as dbStyleSheet } from '../../../../pages/DataBoards/Home/styles';
import { formatCurrency } from '../../../../pages/AdvancedReporting/helpers/formatCurrency';
import { resolveZone } from '../../../../pages/Volunteers/shiftScheduling';
import { baseStyleSheet } from '../../../../styles/styles';
import { AddBoardItemButton } from '../../../boards/AddBoardItemButton';
import { dataGridStyles } from '../../../DataGrid/data-grid-styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { VolunteerIcon } from '../../../svgs/icons/VolunteerIcon';
import { DonatedTimeValueHelp, formatVolunteerHours } from '../../../volunteering/DonatedTimeValueHelp';
import { styleSheet } from './styles';

const STATUS_LABELS: Record<VolunteerAttendanceStatus, string> = {
	[VolunteerAttendanceStatus.Expected]: 'Expected',
	[VolunteerAttendanceStatus.CheckedIn]: 'Checked in',
	[VolunteerAttendanceStatus.CheckedOut]: 'Checked out',
	[VolunteerAttendanceStatus.NoShow]: 'No-show',
	[VolunteerAttendanceStatus.Cancelled]: 'Cancelled',
};

const STATUS_COLORS: Record<VolunteerAttendanceStatus, MultiColorPillProps['color']> = {
	[VolunteerAttendanceStatus.Expected]: 'yellow',
	[VolunteerAttendanceStatus.CheckedIn]: 'blue',
	[VolunteerAttendanceStatus.CheckedOut]: 'green',
	[VolunteerAttendanceStatus.NoShow]: 'red',
	[VolunteerAttendanceStatus.Cancelled]: 'gray',
};

// `date` is midnight on the day the server means, so reading it through a Date shifts it by the
// viewer's UTC offset and lands it on the wrong day. Take the leading YYYY-MM-DD instead.
const formatAttendanceDate = (date: string | undefined) =>
	date ? moment(date.slice(0, 10), 'YYYY-MM-DD').format('MM/DD/YYYY') : '-';

// A row's activity can sit in any timezone and the expanded activity reference does not carry one,
// so scheduled times are shown in the viewer's zone, abbreviation included.
const formatShiftWindow = (row: IVolunteerAttendance) => {
	if (!row.scheduledStartAt) {
		return '-';
	}
	const zone = resolveZone();
	const start = moment.tz(row.scheduledStartAt, zone);
	if (!row.scheduledEndAt) {
		return `${start.format('h:mm A')} ${start.format('z')}`;
	}
	return `${start.format('h:mm A')} – ${moment.tz(row.scheduledEndAt, zone).format('h:mm A')} ${start.format('z')}`;
};

const attendanceDayKey = (row: IVolunteerAttendance) =>
	row.scheduledStartAt
		? moment.tz(row.scheduledStartAt, resolveZone()).format('YYYY-MM-DD')
		: (row.date?.slice(0, 10) ?? '');

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const ShiftTable = ({
	rows,
	roleNamesById,
	onActivityClick,
}: {
	rows: IVolunteerAttendance[];
	roleNamesById: Map<string, string>;
	onActivityClick: (activityId: string) => void;
}) => (
	<table className={css(dataGridStyles.table, styleSheet.tableOverrides)}>
		<tbody>
			{rows.map((row, index) => {
				const status = row.status ?? VolunteerAttendanceStatus.Expected;
				const cellStyles = index === 0 ? dataGridStyles.tableDataCellBorderless : dataGridStyles.tableDataCell;
				const activityTitle = row.activity?.title ?? 'Untitled activity';
				const activityId = row.volunteerActivityId;
				const details = [
					row.roleId ? roleNamesById.get(row.roleId) : undefined,
					row.scheduledStartAt ? formatShiftWindow(row) : undefined,
				].filter((part): part is string => !!part);
				return (
					<Fragment key={row.id}>
						<tr
							{...(activityId
								? {
										'aria-label': `Open ${activityTitle}`,
										className: css(styleSheet.clickableRow),
										onClick: () => onActivityClick(activityId),
										onKeyDown: (ev: KeyboardEvent<HTMLTableRowElement>) => {
											if (ev.key === 'Enter' || ev.key === ' ') {
												ev.preventDefault();
												onActivityClick(activityId);
											}
										},
										role: 'button',
										tabIndex: 0,
									}
								: {})}
						>
							<td className={css(cellStyles)}>
								<span className={css(styleSheet.activityCell)}>
									<span className={css(styleSheet.activityName)}>{activityTitle}</span>
									{row.activity?.isArchived ? (
										<Pill variant='label' color='gray' label='Archived' size='small' />
									) : null}
								</span>
							</td>
							<td className={css(cellStyles)}>
								<Pill variant='label' color={STATUS_COLORS[status]} label={STATUS_LABELS[status]} size='small' />
							</td>
							<td className={css(cellStyles)}>
								<span>{formatAttendanceDate(row.date)}</span>
							</td>
							<td className={css(cellStyles)}>
								<span>{row.hours == null ? '-' : formatVolunteerHours(row.hours)}</span>
							</td>
						</tr>
						{details.length ? (
							<tr>
								<td colSpan={4} className={css(dataGridStyles.tableDataCellBorderless, styleSheet.detailsCell)}>
									{details.map((part, partIndex) => (
										<span key={part}>
											{partIndex > 0 ? ' · ' : null}
											{part}
										</span>
									))}
								</td>
							</tr>
						) : null}
					</Fragment>
				);
			})}
		</tbody>
	</table>
);

const ActivityCell = ({ row, onClick }: { row: IVolunteerAttendance; onClick: (activityId: string) => void }) => {
	if (!row.volunteerActivityId) {
		return <span>{row.activity?.title ?? 'Unknown activity'}</span>;
	}
	const activityId = row.volunteerActivityId;
	return (
		<span className={css(styleSheet.activityCell)}>
			<Button variant='link' styles={[styleSheet.activityLink]} onClick={() => onClick(activityId)}>
				{row.activity?.title ?? 'Untitled activity'}
			</Button>
			{row.activity?.isArchived ? <Pill variant='label' color='gray' label='Archived' size='small' /> : null}
		</span>
	);
};

export const EntityVolunteering = ({ entity }: { entity: Api.IEntity }) => {
	const history = useHistory();
	const filter = useMemo(() => buildContactFilter(entity.id ?? ''), [entity.id]);

	const attendanceQuery = useVolunteerAttendanceQuery({ filter, enabled: !!entity.id });
	const summaryQuery = useVolunteerSummaryQuery({ filter, enabled: !!entity.id });
	const rolesQuery = useVolunteerRolesQuery({ includeArchived: true, enabled: !!entity.id });

	const attendance = useMemo(
		() => attendanceQuery.data?.pages.flatMap(page => page.values) ?? [],
		[attendanceQuery.data]
	);

	const roleNamesById = useMemo(
		() => new Map((rolesQuery.data ?? []).map(role => [role.id, role.name])),
		[rolesQuery.data]
	);

	const nextShift = useMemo(() => {
		const today = moment().format('YYYY-MM-DD');
		// Rows arrive newest first, so the soonest upcoming shift is the last of the leading future block
		// rather than the first row.
		return attendance
			.filter(row => row.status === VolunteerAttendanceStatus.Expected && attendanceDayKey(row) >= today)
			.sort((a, b) => attendanceDayKey(a).localeCompare(attendanceDayKey(b)))[0];
	}, [attendance]);

	const onFetchNextBatch = () => {
		if (attendanceQuery.hasNextPage && !attendanceQuery.isFetchingNextPage) {
			attendanceQuery.fetchNextPage();
		}
	};

	const goToVolunteerActivities = () => history.push(ROUTES.volunteers.home);
	const openActivity = (activityId: string) => history.push(`${ROUTES.volunteers.home}/${activityId}`);

	const renderBody = () => {
		// A disabled query sits at `isPending` forever, so the spinner has to key off a fetch in flight.
		if (attendanceQuery.isLoading) {
			return <LoadingSpinner />;
		}

		// Without this the empty state would tell the viewer this contact has never volunteered when the
		// request simply failed — and the board-disabled case suppresses its toast, so there would be no
		// other signal at all.
		if (attendanceQuery.isError) {
			return <p className={css(styleSheet.emptyText)}>Couldn’t load this contact’s volunteering.</p>;
		}

		if (!attendance.length) {
			return <p className={css(styleSheet.emptyText)}>No volunteering yet.</p>;
		}

		const summary = summaryQuery.data;
		return (
			<>
				{summary ? (
					<p className={css(styleSheet.summaryText)}>
						{[
							`${formatVolunteerHours(summary.totalHours ?? 0)} across ${pluralize(summary.participationCount ?? 0, 'participation')}`,
							pluralize(summary.noShowCount ?? 0, 'no-show'),
							`${formatCurrency(summary.donatedTimeValue ?? 0)} donated time value`,
						].join(' · ')}
						<DonatedTimeValueHelp ratePerHour={summary.donatedTimeValueRatePerHour} />
					</p>
				) : null}

				{nextShift ? (
					<p className={css(styleSheet.summaryText)}>
						{`Next shift: ${[
							formatAttendanceDate(nextShift.date),
							formatShiftWindow(nextShift),
							nextShift.roleId ? roleNamesById.get(nextShift.roleId) : undefined,
						]
							.filter(part => part && part !== '-')
							.join(' · ')} ·`}
						<ActivityCell row={nextShift} onClick={openActivity} />
					</p>
				) : null}

				<ShiftTable rows={attendance} roleNamesById={roleNamesById} onActivityClick={openActivity} />

				{attendanceQuery.hasNextPage && !attendanceQuery.isFetchingNextPage ? (
					<Waypoint bottomOffset='-200px' onEnter={onFetchNextBatch} />
				) : null}
				{attendanceQuery.isFetchingNextPage ? (
					<div className={css(styleSheet.loadMore)}>
						<LoadingSpinner type='small' />
					</div>
				) : null}
			</>
		);
	};

	return (
		<div>
			<div className={css(styleSheet.contentSection)}>
				<header className={css(styleSheet.contentHeader)}>
					<div className={css(dbStyleSheet.boardIconContainer, baseStyleSheet.bgGreen)}>
						<VolunteerIcon fill={THEME.colors.background.light} />
					</div>
					<h6 className={css(styleSheet.contentListTitle)}>Volunteering</h6>
					<AddBoardItemButton className={css(styleSheet.contentAddButton)} onClick={goToVolunteerActivities}>
						Add to an activity
					</AddBoardItemButton>
				</header>

				{renderBody()}
			</div>
		</div>
	);
};
